import React, { useRef } from 'react';
import {
  Printer,
  X,
  CheckCircle2,
  CreditCard,
  FileCheck,
  Shield,
} from 'lucide-react';
import type { Subscription } from '../types';

interface SubscriptionReceiptModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscription: Subscription | null;
}

export const SubscriptionReceiptModal: React.FC<SubscriptionReceiptModalProps> = ({
  isOpen,
  onClose,
  subscription,
}) => {
  const receiptRef = useRef<HTMLDivElement>(null);

  if (!isOpen || !subscription) return null;

  const receiptNumber = `REC-${new Date(subscription.created_at).getFullYear()}-${subscription.id.slice(0, 8).toUpperCase()}`;
  const issueDate = new Date(subscription.start_date || subscription.created_at).toLocaleDateString(
    'pt-PT',
    {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }
  );

  const formatCurrency = (val?: number) => {
    return new Intl.NumberFormat('pt-AO', {
      style: 'currency',
      currency: 'AOA',
      maximumFractionDigits: 0,
    }).format(val || 0);
  };

  const handlePrint = () => {
    window.print();
  };

  const monthlyPrice = Number(subscription.plan?.monthly_price || 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-xs overflow-y-auto print:p-0 print:bg-white print:static">
      <div className="bg-white dark:bg-slate-900 rounded-3xl max-w-2xl w-full border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150 my-8 print:border-none print:shadow-none print:max-w-none print:w-full print:m-0 print:bg-white">
        {/* Action Header - hidden when printing */}
        <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between print:hidden">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-primary-50 dark:bg-primary-950/50 text-primary-600 dark:text-primary-400">
              <FileCheck className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-slate-900 dark:text-white text-base">
                Comprovativo Oficial de Subscrição (Administrativo)
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Fatura-Recibo / Proforma de Serviço Digital para Clientes
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="btn-base btn-sm btn-primary gap-1.5 shadow-sm"
              title="Imprimir ou Guardar em PDF"
            >
              <Printer className="w-4 h-4" />
              <span>Imprimir / PDF</span>
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Printable Receipt Body */}
        <div ref={receiptRef} className="p-8 sm:p-10 space-y-8 print:p-6 print:text-black">
          {/* Header & Logo */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-6 border-b border-slate-200 dark:border-slate-800 print:border-slate-300">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="w-8 h-8 rounded-lg bg-primary-600 text-white font-bold flex items-center justify-center text-sm">
                  TS
                </span>
                <span className="text-xl font-black tracking-tight text-slate-900 dark:text-white print:text-black">
                  TATU SOLUTIONS
                </span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 print:text-slate-600 font-medium">
                Plataforma Corporativa de API Gateway & Marketplace
              </p>
              <p className="text-[11px] text-slate-400 print:text-slate-500 mt-0.5">
                Luanda, Angola • NIF: 5417089123 • suporte@tatusolutions.com
              </p>
            </div>

            <div className="text-left sm:text-right">
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800 uppercase tracking-wider mb-2 print:border-emerald-600 print:bg-emerald-50 print:text-emerald-900">
                <CheckCircle2 className="w-3.5 h-3.5" />
                PAGO / CONFIRMADO
              </span>
              <p className="font-mono text-xs font-bold text-slate-900 dark:text-white print:text-black">
                {receiptNumber}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 print:text-slate-600">
                Data: {issueDate}
              </p>
            </div>
          </div>

          {/* Client & Billing Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm">
            <div className="bg-slate-50 dark:bg-slate-800/40 p-4 rounded-2xl border border-slate-100 dark:border-slate-800/80 print:bg-slate-50 print:border-slate-200">
              <p className="text-[11px] uppercase tracking-wider font-bold text-slate-400 dark:text-slate-500 print:text-slate-500 mb-2">
                Faturado a (Cliente):
              </p>
              <p className="font-bold text-slate-900 dark:text-white print:text-black text-base">
                {subscription.client?.company_name || 'Empresa Cliente'}
              </p>
              <p className="text-xs text-slate-600 dark:text-slate-300 print:text-slate-700 mt-0.5">
                {subscription.client?.email || 'contacto@cliente.ao'}
              </p>
              <p className="text-[11px] font-mono text-slate-400 dark:text-slate-500 print:text-slate-500 mt-1">
                Client ID: {subscription.client_id}
              </p>
            </div>

            <div className="bg-slate-50 dark:bg-slate-800/40 p-4 rounded-2xl border border-slate-100 dark:border-slate-800/80 print:bg-slate-50 print:border-slate-200">
              <p className="text-[11px] uppercase tracking-wider font-bold text-slate-400 dark:text-slate-500 print:text-slate-500 mb-2">
                Aplicação Vinculada:
              </p>
              <p className="font-bold text-slate-900 dark:text-white print:text-black text-base">
                {subscription.application?.name || 'Aplicação Principal'}
              </p>
              <p className="text-xs text-slate-600 dark:text-slate-300 print:text-slate-700 mt-0.5">
                Ambiente:{' '}
                <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                  {subscription.application?.sandbox_mode ? 'Sandbox' : 'Produção'}
                </span>
              </p>
              <p className="text-[11px] font-mono text-slate-400 dark:text-slate-500 print:text-slate-500 mt-1">
                App ID: {subscription.application_id || 'Global'}
              </p>
            </div>
          </div>

          {/* Table of Subscribed Items */}
          <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800 print:border-slate-300">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60 print:bg-slate-100 text-slate-500 dark:text-slate-400 print:text-slate-700 text-xs uppercase font-semibold">
                <tr>
                  <th className="p-3.5">Descrição do Serviço</th>
                  <th className="p-3.5 text-center">Plano</th>
                  <th className="p-3.5 text-center">Quota Mensal</th>
                  <th className="p-3.5 text-right">Valor Pago</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800 print:divide-slate-200">
                <tr>
                  <td className="p-3.5">
                    <div className="font-bold text-slate-900 dark:text-white print:text-black">
                      {subscription.product?.name || 'API Digital'}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 print:text-slate-600">
                      Subscrição mensal com acesso e roteamento através do Kong Gateway
                    </div>
                  </td>
                  <td className="p-3.5 text-center">
                    <span className="inline-block px-2 py-0.5 rounded text-xs font-bold uppercase bg-primary-50 dark:bg-primary-950/40 text-primary-700 dark:text-primary-300 print:bg-slate-200 print:text-black">
                      {subscription.plan?.tier || 'Standard'}
                    </span>
                  </td>
                  <td className="p-3.5 text-center text-xs font-semibold text-slate-700 dark:text-slate-300 print:text-black">
                    {(subscription.plan?.monthly_quota || 0).toLocaleString('pt-PT')} chamadas
                  </td>
                  <td className="p-3.5 text-right font-bold text-slate-900 dark:text-white print:text-black">
                    {formatCurrency(monthlyPrice)}
                  </td>
                </tr>
              </tbody>
              <tfoot className="bg-slate-50/80 dark:bg-slate-800/40 print:bg-slate-100 border-t border-slate-200 dark:border-slate-800 print:border-slate-300 font-bold">
                <tr>
                  <td colSpan={3} className="p-3.5 text-right text-slate-600 dark:text-slate-400 print:text-slate-800">
                    Total Liquidado:
                  </td>
                  <td className="p-3.5 text-right text-base text-emerald-600 dark:text-emerald-400 print:text-black">
                    {formatCurrency(monthlyPrice)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Payment & Security Metadata */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs bg-slate-50 dark:bg-slate-800/30 p-4 rounded-2xl border border-slate-100 dark:border-slate-800/60 print:bg-slate-50 print:border-slate-200">
            <div>
              <p className="font-bold text-slate-700 dark:text-slate-300 print:text-slate-800 mb-1 flex items-center gap-1.5">
                <CreditCard className="w-3.5 h-3.5 text-primary-500" />
                <span>Forma de Liquidação</span>
              </p>
              <p className="text-slate-600 dark:text-slate-400 print:text-slate-700">
                {subscription.payment_method?.name || 'Transferência Bancária / Pagamento Digital'}
              </p>
              <p className="text-[11px] text-slate-400 print:text-slate-500 mt-0.5">
                Validação: Confirmado por Administrador
              </p>
            </div>

            <div>
              <p className="font-bold text-slate-700 dark:text-slate-300 print:text-slate-800 mb-1 flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5 text-emerald-500" />
                <span>Autenticidade Digital</span>
              </p>
              <p className="font-mono text-[10px] text-slate-500 dark:text-slate-400 print:text-slate-600 truncate">
                HASH: SHA256:{subscription.id.replace(/-/g, '').slice(0, 24)}...VERIFIED
              </p>
              <p className="text-[11px] text-slate-400 print:text-slate-500 mt-0.5">
                Documento emitido eletronicamente pela plataforma.
              </p>
            </div>
          </div>

          {/* Footer note */}
          <div className="text-center pt-2 text-[11px] text-slate-400 print:text-slate-500">
            Este comprovativo certifica a ativação da quota de consumo da API no ambiente do Kong Gateway. Para esclarecimentos, contacte suporte@tatusolutions.com.
          </div>
        </div>

        {/* Action Footer */}
        <div className="p-5 bg-slate-50 dark:bg-slate-800/40 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between print:hidden">
          <div className="text-xs text-slate-500 dark:text-slate-400">
            Documento fiscal e operacional oficial para entrega ao cliente.
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="btn-base btn-md btn-primary gap-2 shadow-sm"
            >
              <Printer className="w-4 h-4" />
              <span>Imprimir / Salvar PDF</span>
            </button>
            <button onClick={onClose} className="btn-base btn-md btn-secondary">
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
